import {
    IUserService
} from "./user.service.interface";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {IDotRezUserSession} from "../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {TimeSpan} from "../../types/time-span";
import {makeObservable, observable, reaction} from "mobx";
import {IUserModel} from "./models/user-model.interface";
import {AnonymousUserModel} from "./models/anonymous-user/anonymous-user.model";
import {NullableNumber} from "../../types/nullable-types";
import {Check} from "../../types/type-checking";


const RECREATE_SESSION_INTERVAL = TimeSpan.fromMinutes(13);
const ACCELERATED_RECREATE_SESSION_INTERVAL = TimeSpan.fromSeconds(6);

export class UserService extends ServiceBase implements IUserService {
    constructor(services: IServiceFactory) {
        super(services);

        this._currentUser = new AnonymousUserModel(this.services);

        this._currentUser.getSession().then(() => {
            this._lastSuccessfulRecreateSession = Date.now();
            this._startRecreateSessionTimer(RECREATE_SESSION_INTERVAL);
        }).catch(() => {
            this._startRecreateSessionTimer(ACCELERATED_RECREATE_SESSION_INTERVAL);
        });

        reaction(() => this.services.application.isActive,
            async (isActive) => {
                if(!isActive) {
                    return;
                }

                // if _lastSuccessfulRecreateSession was not set yet it means that there is a pending create session
                if(Check.isNullOrUndefined(this._lastSuccessfulRecreateSession)) {
                    return;
                }

                const now = this.services.time.currentDate.getTime();
                const diff = now - this._lastSuccessfulRecreateSession;
                if (diff >= RECREATE_SESSION_INTERVAL.totalMilliseconds) {
                    await this._tryRecreateSession();
                } else {
                    this._startRecreateSessionTimer(TimeSpan.fromMilliseconds(RECREATE_SESSION_INTERVAL.totalMilliseconds - diff));
                }
            });



        makeObservable(this, {
            _currentUser: observable.ref
        });
    }

    private _recreateSessionTimer: any = null;
    private _lastSuccessfulRecreateSession: NullableNumber = null;
    _currentUser: IUserModel;

    getSession(): Promise<IDotRezUserSession> {
        return this._currentUser.getSession();
    }

    private async _tryRecreateSession(): Promise<void> {
        clearTimeout(this._recreateSessionTimer);

        try {
            await this._currentUser.reCreateSession();
            this._lastSuccessfulRecreateSession = this.services.time.currentDate.getTime();
            this._startRecreateSessionTimer(RECREATE_SESSION_INTERVAL);
        } catch (err) {
            this.services.logger.error('Failed to recreate user session', err);
            this._startRecreateSessionTimer(ACCELERATED_RECREATE_SESSION_INTERVAL);
        }
    }

    private _startRecreateSessionTimer(timeInterval: TimeSpan): void {
        clearTimeout(this._recreateSessionTimer);
        this._recreateSessionTimer = setTimeout(async () => {
            await this._tryRecreateSession();
        }, timeInterval.totalMilliseconds);
    }


}
